import intersection from 'lodash/intersection';

import {
  adminAkelaRoles,
  canApproveSBRoles,
  canEditCalendarRoles,
  canEditParticipationStatusRoles,
  canEditPaymentLogsRoles,
  canEditPermissionsManagerRoles,
  canEditSubCalendarRoles,
  canEditUnitPaymentLogsRoles,
  canEditYouthProfileRoles,
  mBCounselorRoles,
  organizationRoles,
  scoutbookRoles,
  specialLogInRoles,
  subUnitRoles,
} from './constants';
import type { DedupedRoleType } from './types';

export const hasAdminRights = ({ role, roleTypes }: DedupedRoleType) =>
  organizationRoles.ADMIN.includes(role) ||
  roleTypes.some(({ roleType }) => organizationRoles.ADMIN.includes(roleType));

export const hasSpecialLoginRole = ({ role }: DedupedRoleType) =>
  (specialLogInRoles as string[]).includes(role);

export const hasAdvancementsCoordinatorRights = (org: { role: string }) =>
  org.role === organizationRoles.ADVANCEMENT_COORDINATOR;

export const getCouncilRights = ({ role }: DedupedRoleType) => {
  const hasCouncilEditRights = organizationRoles.COUNCIL_ADMINS.includes(role);
  const isCouncil =
    hasCouncilEditRights ||
    organizationRoles.DISTRICT_EXECUTIVES.includes(role);

  return {
    isCouncil,
    hasCouncilEditRights,
  };
};

export const hasCouncilRights = (org: DedupedRoleType) =>
  getCouncilRights(org).isCouncil;

export const hasMeritBadgeCounselRights = ({ role }: DedupedRoleType) =>
  organizationRoles.MERIT_BADGE_COUNSELOR === role;

export const hasYouthMemberRights = ({ role, roleTypes }: DedupedRoleType) =>
  organizationRoles.YOUTH_MEMBER.includes(role) ||
  roleTypes.some(({ roleType }) =>
    organizationRoles.YOUTH_MEMBER.includes(roleType),
  );

export const hasParentGuardianRights = ({ role, roleTypes }: DedupedRoleType) =>
  organizationRoles.PARENT_GUARDIAN.includes(role) ||
  roleTypes.some(({ roleType }) =>
    organizationRoles.PARENT_GUARDIAN.includes(roleType),
  );

export const hasCalendarEditorRights = ({ role, roleTypes }: DedupedRoleType) =>
  organizationRoles.CALENDAR_EDITOR.includes(role) ||
  roleTypes.some(({ roleType }) =>
    organizationRoles.CALENDAR_EDITOR.includes(roleType),
  );

export const hasAdminAkelaRole = ({ role, roleTypes }: DedupedRoleType) =>
  adminAkelaRoles.includes(role) ||
  roleTypes.some(({ roleType }) => adminAkelaRoles.includes(roleType));

export const hasApproverSBRole = ({ role, roleTypes }: DedupedRoleType) =>
  canApproveSBRoles.includes(role) ||
  roleTypes.some(({ roleType }) => canApproveSBRoles.includes(roleType));

export const hasScoutbookRole = ({ role, roleTypes }: DedupedRoleType) =>
  scoutbookRoles.includes(role) ||
  roleTypes.some(({ roleType }) => scoutbookRoles.includes(roleType));

export const hasSubUnitsRights = ({
  role,
  roleTypes,
  allRoles = [],
}: DedupedRoleType) =>
  subUnitRoles.includes(role) ||
  roleTypes.some(({ roleType }) => subUnitRoles.includes(roleType)) ||
  allRoles.some(rolName => subUnitRoles.includes(rolName));

export const hasMBCRights = ({
  role,
  roleTypes,
  allRoles = [],
}: DedupedRoleType) =>
  mBCounselorRoles.includes(role) ||
  roleTypes.some(({ roleType }) => mBCounselorRoles.includes(roleType)) ||
  allRoles.some(rolName => mBCounselorRoles.includes(rolName));

export const hasCalendarPerms = ({
  role,
  roleTypes,
  allRoles = [],
}: DedupedRoleType) =>
  canEditCalendarRoles.includes(role) ||
  organizationRoles.CALENDAR_EDITOR.includes(role) ||
  roleTypes.some(({ roleType }) => canEditCalendarRoles.includes(roleType)) ||
  allRoles.some(rolName => canEditCalendarRoles.includes(rolName));

export const hasSubCalendarPerms = ({
  role,
  roleTypes,
  allRoles = [],
}: DedupedRoleType) =>
  canEditSubCalendarRoles.includes(role) ||
  roleTypes.some(({ roleType }) =>
    canEditSubCalendarRoles.includes(roleType),
  ) ||
  allRoles.some(rolName => canEditSubCalendarRoles.includes(rolName));

export const hasEditYouthProfileRolesPerms = ({
  role,
  roleTypes,
  allRoles,
}: DedupedRoleType) =>
  canEditYouthProfileRoles.includes(role) ||
  roleTypes.some(({ roleType }) =>
    canEditYouthProfileRoles.includes(roleType),
  ) ||
  allRoles.some(rolName => canEditYouthProfileRoles.includes(rolName));

export const hasEditPaymentLogsPerms = ({ allRoles }: DedupedRoleType) =>
  allRoles.some(name => canEditPaymentLogsRoles.includes(name));

export const hasEditUnitPaymentLogsPerms = ({ allRoles }: DedupedRoleType) =>
  allRoles.some(name => canEditUnitPaymentLogsRoles.includes(name));

export const hasPermissionsManagerRights = ({
  role,
  roleTypes,
  allRoles = [],
}: DedupedRoleType) =>
  canEditPermissionsManagerRoles.includes(role) ||
  organizationRoles.PERMISSIONS_MANAGER.includes(role) ||
  roleTypes.some(({ roleType }) =>
    canEditPermissionsManagerRoles.includes(roleType),
  ) ||
  allRoles.some(rolName => canEditPermissionsManagerRoles.includes(rolName));

export function hasEditOptOutPerms(
  userRole: Pick<DedupedRoleType, 'role' | 'roleTypes' | 'allRoles'>,
) {
  if (canEditParticipationStatusRoles.includes(userRole.role)) return true;

  const acceptedRoles = [
    ...(userRole.roleTypes?.map(item => item.roleType) || []),
    ...(userRole.allRoles || []),
  ].filter(Boolean);
  const validFound = intersection(
    acceptedRoles,
    canEditParticipationStatusRoles,
  );
  return validFound.length > 0;
}
